import type { CollectionConfig, CowryTraits, PatternParams, PbrParams } from "../core/types";
import { createRandomSource } from "../rng/rng";
import { deriveGeometryParams } from "../geometry/deriveGeometryParams";

export function deriveCowryTraits(
  collection: CollectionConfig,
  itemSeed: string,
): CowryTraits {
  const root = createRandomSource(itemSeed);
  const size = root.fork("size").weighted(collection.sizeWeights);
  const geometry = deriveGeometryParams(itemSeed, collection.geometryConstraints);
  const sizeScale = collection.sizeScale[size];
  geometry.shellLength *= sizeScale;
  geometry.shellWidth *= sizeScale;
  geometry.shellHeight *= sizeScale;

  const identity = root.fork("identity");
  const formFamily = identity.fork("form").weighted(collection.formFamilies);
  const finishFamily = identity.fork("finish").weighted(collection.finishFamilies);
  const patternFamily = identity.fork("pattern").weighted(collection.patternFamilies);
  const paletteFamily = identity.fork("palette").weighted(collection.paletteFamilies);

  return {
    size,
    formFamily,
    finishFamily,
    patternFamily,
    paletteFamily,
    ventralStyle: collection.ventralStyle,
    rarity: collection.rarityForSize[size],
    geometry,
    pattern: derivePatternParams(root.fork("pattern-params")),
    pbr: derivePbrParams(collection, finishFamily, root.fork("pbr")),
  };
}

function derivePatternParams(random: ReturnType<typeof createRandomSource>): PatternParams {
  return {
    frequency: random.range(4.5, 10.5),
    warp: random.range(0.08, 0.32),
    rotation: random.range(-Math.PI, Math.PI),
    contrast: random.range(0.55, 0.92),
    secondaryScale: random.range(0.35, 0.8),
  };
}

function derivePbrParams(
  collection: CollectionConfig,
  finishFamily: string,
  random: ReturnType<typeof createRandomSource>,
): PbrParams {
  const finish = collection.finishDefinitions[finishFamily];
  if (!finish) throw new Error(`Collection finish definition is missing: ${finishFamily}`);
  return {
    roughness: random.range(finish.roughness.min, finish.roughness.max),
    metalness: random.range(finish.metalness.min, finish.metalness.max),
    normalStrength: random.range(0.15, 0.6),
    clearcoat: random.range(0.2, 0.7),
    clearcoatRoughness: random.range(0.12, 0.34),
  };
}
